'use client';

import { ArrowIcon, RefreshIcon } from '@insta-club/components';
import { Button, CustomBuffering, ProgressBar, RangeSlider, ToggleMutedBtn } from '@insta-club/ui';
import { formatTime } from '@insta-club/utilities';
import Hls from 'hls.js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { VideoProtection } from './video-protection';

interface Props {
    mp4Url?: string;
    hlsUrl?: string;
    poster?: string;
    muted?: boolean;
    loop?: boolean;
    autoPlay?: boolean;
    autoPlayPaused?: boolean;
    showEndDialog?: boolean;
    onView?: () => void;
    onToggleMute?: (muted: boolean) => void;
    onTimeupdate?: (time: number) => void;
    onTimeupdateDelay?: (time: number) => void;
    onEnded?: () => void;
    onBack?: () => void;
    onRestart?: () => void;
    duration?: number;
    showProgress?: boolean;
    disableProgress?: boolean;
    enableProtection?: boolean;
    watermarkText?: string;
}

const CustomVideo: React.FC<Props> = ({
    mp4Url,
    hlsUrl,
    poster,
    muted = true,
    loop = true,
    autoPlay = true,
    autoPlayPaused = false,
    showEndDialog = false,
    onView,
    onToggleMute,
    onTimeupdate,
    onTimeupdateDelay,
    onEnded,
    onBack,
    onRestart,
    duration = 0,
    showProgress = true,
    disableProgress = true,
    enableProtection = false,
    watermarkText = ''
}) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const observedTime = useRef(0);
    const [isMuted, setIsMuted] = useState(muted);
    const [isBuffering, setIsBuffering] = useState(false);
    const [isEnded, setIsEnded] = useState(false);
    const [progress, setProgress] = useState(0);
    const [videoDuration, setVideoDuration] = useState(0);
    const [isLoaded, setIsLoaded] = useState(false);

    const handleToggleMute = useCallback(() => {
        const newMuted = !isMuted;
        setIsMuted(newMuted);
        onToggleMute?.(newMuted);
        if (videoRef.current) videoRef.current.muted = newMuted;
    }, [isMuted, onToggleMute]);

    const handleTimeupdate = useCallback(() => {
        const video = videoRef.current;
        if (!video) return;

        const { currentTime } = video;
        const intTime = Math.floor(currentTime);

        setProgress((currentTime / videoDuration) * 100);
        onTimeupdate?.(intTime);

        const step = videoDuration / 4;
        const nextMilestone = Math.floor(currentTime / step);

        if (nextMilestone > Math.floor(observedTime.current / step)) {
            onTimeupdateDelay?.(intTime);
            observedTime.current = currentTime;
        }
    }, [videoDuration, onTimeupdate, onTimeupdateDelay]);

    const handleEnded = useCallback(() => {
        if (showEndDialog) setIsEnded(true);
        onEnded?.();
    }, [showEndDialog, onEnded]);

    const handleBack = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            onBack?.();
        },
        [onBack]
    );

    const handleRestart = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            const video = videoRef.current;
            if (!video) return;

            observedTime.current = 0;
            video.currentTime = 0;
            video?.play();
            setIsEnded(false);
            onRestart?.();
        },
        [onRestart]
    );

    const handleProgressChange = (newValue: number) => {
        if (!videoRef.current) return;
        const video = videoRef.current;
        const seekTime = (newValue / 100) * videoDuration;
        video.currentTime = seekTime;
        setProgress(newValue);
    };

    useEffect(() => {
        if (!videoRef.current) return;
        const video = videoRef.current;
        let hls: Hls | null = null;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    if (!video.src) {
                        if (hlsUrl) {
                            if (Hls.isSupported()) {
                                hls = new Hls();
                                hls.loadSource(hlsUrl);
                                hls.attachMedia(video);
                            } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
                                video.src = hlsUrl;
                            } else if (mp4Url) {
                                video.src = mp4Url;
                            }
                        } else if (mp4Url) {
                            video.src = mp4Url;
                        }
                    }
                    onView?.();
                    if (autoPlayPaused || autoPlay) video?.play();
                } else {
                    if (!video.paused && autoPlayPaused) video?.pause();
                }
            },
            { threshold: 0.75 }
        );
        observer.observe(video);
        return () => {
            observer.unobserve(video);
            if (hls) {
                hls.destroy();
                hls = null;
            }
        };
    }, [mp4Url, hlsUrl, autoPlayPaused, autoPlay, onView]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        const handleLoadedMetadata = () => {
            video.currentTime = duration;
            setVideoDuration(video.duration || 0);
        };
        const handleCanPlay = () => {
            setIsLoaded(true);
        };
        video.addEventListener('loadedmetadata', handleLoadedMetadata);
        video.addEventListener('canplay', handleCanPlay);
        return () => {
            video.removeEventListener('loadedmetadata', handleLoadedMetadata);
            video.removeEventListener('canplay', handleCanPlay);
        };
    }, [duration]);

    useEffect(() => {
        setIsMuted(muted);
        if (videoRef.current) videoRef.current.muted = muted;
    }, [muted]);

    const videoContent = (
        <div className='relative h-full w-full cursor-pointer overflow-hidden bg-black'>
            <video
                ref={videoRef}
                controls={false}
                muted={isMuted}
                poster={poster}
                autoPlay={autoPlay}
                onWaiting={() => setIsBuffering(true)}
                onPlaying={() => setIsBuffering(false)}
                onTimeUpdate={handleTimeupdate}
                onEnded={handleEnded}
                preload='none'
                x5-playsinline='true'
                webkit-playsinline='true'
                className={`h-full w-full transition-opacity duration-700 ease-out ${
                    isLoaded ? 'opacity-100' : 'opacity-0'
                }`}
                playsInline
                loop={loop}
            />
            {!isLoaded && (
                <div className='absolute inset-0 flex items-center justify-center bg-black'>
                    <CustomBuffering isBuffering />
                </div>
            )}
            <div className='absolute inset-0 flex h-full w-full flex-col justify-between'>
                <div className='flex-1' onClick={handleToggleMute}>
                    <CustomBuffering isBuffering={isBuffering} />
                    <ToggleMutedBtn isMuted={isMuted} />
                    {isEnded && (
                        <div
                            onClick={(e) => e.stopPropagation()}
                            className='bg-opacity-85 absolute inset-0 z-50 flex flex-col items-center justify-center gap-8 bg-black'
                        >
                            <span className='text-center text-base font-semibold text-white'>
                                این ویدیو به پایان رسیده است
                            </span>
                            <div className='flex items-center justify-center gap-4'>
                                <Button
                                    size='sm'
                                    className='px-4 text-xs'
                                    onClick={handleRestart}
                                    icon={<RefreshIcon width={16} height={16} />}
                                >
                                    پخش دوباره
                                </Button>
                                <Button
                                    size='sm'
                                    className='px-4 text-xs'
                                    variant='light'
                                    onClick={handleBack}
                                    icon={<ArrowIcon width={16} height={16} />}
                                >
                                    بازگشت
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
                {showProgress && disableProgress ? (
                    <ProgressBar percentage={progress} className='bg-gray-400' barClassName='bg-white' />
                ) : (
                    <div
                        className='absolute right-0 bottom-0 left-0 z-50 px-4 select-none'
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className='flex items-center gap-2'>
                            <span className='!font-sans text-xs font-semibold text-white'>
                                {formatTime(videoDuration)}
                            </span>
                            <RangeSlider value={progress} onChange={handleProgressChange} />
                            <span className='!font-sans text-xs font-semibold text-white'>
                                {formatTime(videoRef.current?.currentTime || 0)}
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    return enableProtection ? (
        <VideoProtection
            watermarkText={watermarkText}
            videoRef={videoRef}
            pauseOnRecording={true}
            showWarning={true}
        >
            {videoContent}
        </VideoProtection>
    ) : (
        videoContent
    );
};

export default CustomVideo;

